import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Reads/writes filter, sort, and pagination state as URL query params, so a
// filtered admin list (Students, Classes, Safety, ...) is deep-linkable and
// survives opening/closing a SideDrawer or hitting browser back/forward —
// instead of living in component state that resets the moment the view
// unmounts.
//
// `defaults` doubles as the schema: a key's default value's *type* decides
// how its query-string value is coerced back out of the URL (number ->
// Number(...), array -> comma-split, anything else -> left as a string),
// and a value that equals its default is omitted from the URL entirely so
// "no filters applied" reads as a clean `/admin/students`, not
// `?role=ALL&school=&q=&page=1`.
//
// Returns `[state, setQueryState]`. `state` always has every key `defaults`
// declares, sourced from the URL when present and coerced/validated against
// that key's default type; an unparsable value (e.g. `?page=abc`) falls
// back to the default rather than propagating NaN/garbage into the view.
//
// `setQueryState(updates, opts)` takes a partial update — pass multiple
// keys in one call (e.g. changing a filter *and* resetting `page` back to
// 1 together) to land them as a single history entry, not one per key.
// `opts.push: true` opts into a "real" navigation (a new history entry the
// user would expect Back to undo, e.g. picking a different saved view);
// omitted/false replaces the current entry, which is what keeps Back/
// Forward from stepping through every keystroke of a search box or every
// column sort click.
export function useQueryState(defaults) {
  const location = useLocation();
  const navigate = useNavigate();

  const state = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const result = {};
    Object.entries(defaults).forEach(([key, defaultValue]) => {
      const raw = params.get(key);
      if (raw === null || raw === '') {
        result[key] = defaultValue;
        return;
      }
      if (typeof defaultValue === 'number') {
        const parsed = Number(raw);
        result[key] = Number.isFinite(parsed) ? parsed : defaultValue;
      } else if (Array.isArray(defaultValue)) {
        result[key] = raw.split(',').filter(Boolean);
      } else {
        result[key] = raw;
      }
    });
    return result;
  }, [location.search, defaults]);

  const setQueryState = useCallback((updates, { push = false } = {}) => {
    const params = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      const defaultValue = defaults[key];
      const isEmpty =
        value === undefined ||
        value === null ||
        value === '' ||
        (typeof value === 'number' && Number.isNaN(value)) ||
        (Array.isArray(value) && value.length === 0);
      const isDefaultValue = Array.isArray(defaultValue)
        ? Array.isArray(value) &&
          value.length === defaultValue.length &&
          value.every((v) => defaultValue.includes(v))
        : value === defaultValue;
      if (isEmpty || isDefaultValue) {
        params.delete(key);
      } else if (Array.isArray(value)) {
        params.set(key, value.join(','));
      } else {
        params.set(key, String(value));
      }
    });
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: !push }
    );
  }, [location.pathname, location.search, navigate, defaults]);

  return [state, setQueryState];
}

// Restores a scrollable list's scroll position when returning to the same
// `pathname + search` — e.g. opening a row's SideDrawer and closing it, or
// browser back/forward into a filtered list — instead of every navigation
// snapping the list back to the top. sessionStorage (not localStorage), so
// it clears with the tab like the rest of this app's per-session UI state
// (see api/authFetch.js's TOKEN_KEY comment for the same reasoning applied
// to the auth token).
//
// Usage:
//   const scrollRef = useRef(null);
//   useScrollRestore(scrollRef);
//   <Box ref={scrollRef} sx={{ overflowY: 'auto' }}>...</Box>
//
// `key` is optional — pass an explicit one (e.g. a drawer's own id) when
// the default `pathname + search` key is too coarse for what should count
// as "the same list" (multiple independently-scrollable panes at one URL).
const SCROLL_STORAGE_PREFIX = 'admin_scroll:';

export function useScrollRestore(scrollRef, key) {
  const location = useLocation();
  const storageKey = `${SCROLL_STORAGE_PREFIX}${key || `${location.pathname}${location.search}`}`;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    let saved = null;
    try {
      saved = window.sessionStorage.getItem(storageKey);
    } catch {
      // Storage disabled (private browsing, locked-down lab machine) —
      // nothing to restore, and the write below will no-op the same way.
    }
    if (saved !== null) {
      const y = Number(saved);
      if (Number.isFinite(y)) el.scrollTop = y;
    }

    // rAF-throttled: a raw scroll listener can fire dozens of times a
    // second, and every extra sessionStorage.setItem is a synchronous
    // main-thread write for no benefit — only the *latest* position ever
    // matters once the user stops scrolling.
    let frame = null;
    const handleScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        try {
          window.sessionStorage.setItem(storageKey, String(el.scrollTop));
        } catch {
          // Storage disabled/full — scroll restore silently no-ops.
        }
      });
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [scrollRef, storageKey]);
}

export default useQueryState;
